import { onMounted, onUnmounted, reactive, ref } from 'vue';
import type { PopupInstance } from './Popup.model';

type PopupDragState = {
	id: string;
	startX: number;
	startY: number;
	originX: number;
	originY: number;
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
};

const STACK_OFFSET = 12;

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);

const isInteractiveDragTarget = (target: EventTarget | null) =>
	target instanceof Element &&
	Boolean(
		target.closest(
			'.popup__header-actions, button, a, input, select, textarea, [role="button"], [data-popup-drag-ignore]'
		)
	);

export const usePopupDrag = () => {
	const popupDragOffsets = reactive<Record<string, { x: number; y: number }>>(
		{}
	);
	const activeDrag = ref<PopupDragState | null>(null);

	const getPopupStackStyle = (index: number) => ({
		'--popup-stack-index': `${index}`,
		'--popup-stack-offset-x': `${index * STACK_OFFSET}px`,
		'--popup-stack-offset-y': `${index * STACK_OFFSET}px`,
		'--popup-stack-transform': `translate(${index * STACK_OFFSET}px, ${
			index * STACK_OFFSET
		}px)`,
		'--popup-background-transparency': index === 0 ? '80%' : '95%',
		zIndex: `${200 + index}`,
	});

	const applyPopupDragOffset = (popupId: string, x: number, y: number) => {
		popupDragOffsets[popupId] = { x, y };
		const popupElement = document.getElementById(popupId);
		if (!popupElement) {
			return;
		}

		popupElement.style.setProperty('--popup-drag-x', `${x}px`);
		popupElement.style.setProperty('--popup-drag-y', `${y}px`);
	};

	const startPopupDrag = (event: PointerEvent, popup: PopupInstance) => {
		if (event.button !== 0 || isInteractiveDragTarget(event.target)) {
			return;
		}

		const container = (event.currentTarget as HTMLElement | null)?.closest(
			'.popup__container'
		);
		if (!(container instanceof HTMLElement)) {
			return;
		}

		const rect = container.getBoundingClientRect();
		const currentOffset = popupDragOffsets[popup.id] || { x: 0, y: 0 };

		activeDrag.value = {
			id: popup.id,
			startX: event.clientX,
			startY: event.clientY,
			originX: currentOffset.x,
			originY: currentOffset.y,
			minX: currentOffset.x - rect.left,
			maxX: currentOffset.x + window.innerWidth - rect.right,
			minY: currentOffset.y - rect.top,
			maxY: currentOffset.y + window.innerHeight - rect.bottom,
		};

		event.preventDefault();
		(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(
			event.pointerId
		);
	};

	const handlePopupDragMove = (event: PointerEvent) => {
		const drag = activeDrag.value;
		if (!drag) {
			return;
		}

		applyPopupDragOffset(
			drag.id,
			clamp(drag.originX + event.clientX - drag.startX, drag.minX, drag.maxX),
			clamp(drag.originY + event.clientY - drag.startY, drag.minY, drag.maxY)
		);
	};

	const stopPopupDrag = () => {
		activeDrag.value = null;
	};

	onMounted(() => {
		window.addEventListener('pointermove', handlePopupDragMove);
		window.addEventListener('pointerup', stopPopupDrag);
		window.addEventListener('pointercancel', stopPopupDrag);
	});

	onUnmounted(() => {
		window.removeEventListener('pointermove', handlePopupDragMove);
		window.removeEventListener('pointerup', stopPopupDrag);
		window.removeEventListener('pointercancel', stopPopupDrag);
	});

	return {
		getPopupStackStyle,
		startPopupDrag,
	};
};
